import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';

interface DashboardNavConfig {
  enabled: boolean;
  label: string;
}

/**
 * Hook that fetches the dashboard navigation config on mount.
 * Returns the config state, loading indicator, and a refresh function.
 * On fetch failure, defaults to { enabled: false, label: '' } so the nav item stays hidden.
 */
export function useDashboardNavConfig(): {
  config: DashboardNavConfig;
  loading: boolean;
  refresh: () => Promise<void>;
} {
  const [config, setConfig] = useState<DashboardNavConfig>({ enabled: false, label: '' });
  const [loading, setLoading] = useState(true);

  const fetchConfig = useCallback(async () => {
    try {
      const data = await apiClient<DashboardNavConfig>('/dashboard-nav-config');
      setConfig(data);
    } catch {
      setConfig({ enabled: false, label: '' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const refresh = useCallback(async () => {
    await fetchConfig();
  }, [fetchConfig]);

  return { config, loading, refresh };
}
